import { injectable, inject } from 'inversify';
import { Observable, from, map, switchMap } from 'rxjs';

import { StepUpModalComponent } from '../components/ui/login/step-up-modal.component';
import { ApiConfig } from './api-config';
import { AuthService } from './auth.service';
import { LightHttp } from './light-http.service';

/**
 * sha256 hex digest — IDENTICAL to login.service.ts's helper. The backend
 * compares the reauth password against the SAME hash login/register submit, so
 * the FE must hash the same way. (Kept private here to avoid coupling to
 * login.service's internal, non-exported function.)
 */
async function sha256(message: string): Promise<string> {
    if (!message) {
        return message;
    }
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => ('00' + b.toString(16)).slice(-2)).join('');
}

/**
 * Step-up (re-authentication) service. Distinct from the SILENT refresh path:
 * a `step_up_required` 401 means the backend wants the user to PROVE possession
 * of their password before a sensitive action proceeds. We prompt for the
 * password (interactive modal), POST it to `/auth/reauth`, swap in the
 * elevated bearer, and let the caller (light-http) replay the original request
 * ONCE.
 */
@injectable()
export class StepUpService {
    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(AuthService) private authService: AuthService,
    ) {}

    /**
     * Prompt the user for their current password, then re-authenticate. Resolves
     * (completes) once the elevated bearer is installed; errors on cancel or a
     * wrong password (reauth 401) so light-http propagates the original failure.
     */
    elevate(): Observable<void> {
        return this.promptPassword().pipe(switchMap((pw) => this.reauth(pw)));
    }

    /**
     * Opens StepUpModalComponent and resolves the entered password. Errors when
     * the user cancels / dismisses (so elevate() does not attempt a reauth).
     * Split out from reauth() so the network path is unit-testable without React.
     */
    private promptPassword(): Observable<string> {
        return new Observable<string>((subscriber) => {
            StepUpModalComponent.show({
                onSubmit: (password: string) => {
                    if (!password) {
                        subscriber.error(new Error('step-up cancelled'));
                        return;
                    }
                    subscriber.next(password);
                    subscriber.complete();
                },
                onCancel: () => {
                    subscriber.error(new Error('step-up cancelled'));
                },
            });
        });
    }

    /**
     * Hash the password the SAME way login.service does, then POST it to
     * /auth/reauth. On success swaps in the elevated bearer via
     * authService.setAuthToken. On a wrong password the backend returns 401 →
     * propagate.
     *
     * `__isAuthRetry: true` ensures THIS reauth POST is never itself intercepted
     * by light-http's step-up branch (belt-and-braces: /auth/reauth is also an
     * auth endpoint, which the branch already excludes).
     */
    private reauth(password: string): Observable<void> {
        return from(sha256(password)).pipe(
            switchMap((hashedPw) =>
                this.http
                    .request<{ error: boolean; bearer: string }>(
                        this.apiConfig.getReauthEndpoint(),
                        'POST',
                        { password: hashedPw },
                        {
                            withCredentials: true,
                            headers: { 'X-Requested-With': 'XMLHttpRequest' },
                            __isAuthRetry: true,
                        },
                    )
                    .response$.pipe(
                        map((res) => {
                            this.authService.setAuthToken(res.bearer);
                            return undefined;
                        }),
                    ),
            ),
        );
    }
}
